import React, { useState } from "react";
import { openBillingPortal, reactivateSubscription } from "../../auth/firebaseAuth";

// Reads a tenant's plan/status and shows the right billing message. Admins get
// a "Manage billing" button that opens the Stripe portal. Also exports a helper
// to decide when the board should be blocked entirely.
export function isBillingBlocked(tenant) {
  return !!tenant && tenant.plan !== "comp" && ["canceled", "unpaid"].includes(tenant.status);
}

function daysLeft(trialEndsAt) {
  if (!trialEndsAt || !trialEndsAt.toMillis) return null;
  return Math.max(0, Math.ceil((trialEndsAt.toMillis() - Date.now()) / 86400000));
}

function ManageBillingButton({ small }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const { url } = await openBillingPortal(); window.location.assign(url); }
    catch { setBusy(false); }
  };
  return (
    <button onClick={go} disabled={busy}
      style={{ marginLeft: small ? "0.6em" : 0, fontSize: small ? "70%" : "0.9em", cursor: "pointer" }}>
      {busy ? "Opening…" : "Manage billing"}
    </button>
  );
}

const bar = (bg) => ({
  background: bg, color: "white", padding: "0.5em 1em", textAlign: "center",
  fontSize: "0.9em", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4em",
});

function ReactivateButton() {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const { url } = await reactivateSubscription(); window.location.assign(url); }
    catch { setBusy(false); }
  };
  return (
    <button onClick={go} disabled={busy}
      style={{ fontSize: "1em", cursor: "pointer", padding: "0.5em 1.1em", marginRight: "0.6em" }}>
      {busy ? "Opening…" : "Reactivate subscription"}
    </button>
  );
}

// Full-screen gate shown when access is blocked (canceled / unpaid).
export function BillingGate({ tenant, isAdmin }) {
  return (
    <div style={{ color: "white", textAlign: "center", padding: "3em 1.5em", maxWidth: 520, margin: "2em auto" }}>
      <h3>Your subscription is inactive</h3>
      <p style={{ color: "#c7cdd6" }}>
        This care circle's subscription has ended.{" "}
        {isAdmin ? "Reactivate to restore access for everyone." : "Please ask your care circle's admin to reactivate it."}
      </p>
      {isAdmin && (
        <div style={{ marginTop: "0.5em" }}>
          <ReactivateButton />
          <ManageBillingButton />
        </div>
      )}
    </div>
  );
}

export default function BillingBanner({ tenant, isAdmin }) {
  if (!tenant) return null;
  if (tenant.plan === "comp") return null;

  if (tenant.status === "trialing") {
    const d = daysLeft(tenant.trialEndsAt);
    return (
      <div style={bar("#2f5233")}>
        <span>🎉 Free trial{d != null ? ` — ${d} day${d === 1 ? "" : "s"} left` : ""}. You won't be charged until it ends.</span>
        {isAdmin && <ManageBillingButton small />}
      </div>
    );
  }
  if (tenant.status === "past_due") {
    return (
      <div style={bar("#6b4a13")}>
        <span>⚠️ A payment didn't go through — update your card to avoid losing access.</span>
        {isAdmin && <ManageBillingButton small />}
      </div>
    );
  }
  return null; // active: no banner (billing lives in the portal)
}
